import { z } from "zod";

import capabilityFileIcon from "@/assets/figma/chat-extensions/file.svg";
import { emptyMerryChatAddMenuCatalog } from "@/features/chat/chatAddMenuCatalog";
import type { ChatAttachmentResourceRef } from "@/features/chat/chatAttachments";
import type {
  MerryChatAddMenuCapability,
  MerryChatAddMenuCatalog,
  MerryChatAddMenuTool,
} from "@/shared/ui/MerryChatAddMenu";
import type {
  MerryChatExtensionParameters,
  MerryChatExtensionPayload,
  MerryChatExtensionSelection,
  MerryChatExtensionViewModel,
} from "@/shared/ui/chatExtensionTypes";
import type {
  MerryImageParameters,
  MerryVideoParameters,
} from "@/shared/ui/merryChatAddMenuVariants";
import { merryImageOutputDimensions } from "@/shared/ui/merryChatAddMenuVariants";
import { ChatInputError } from "@/shared/ui/chatInputError";

const imageExtensionParametersSchema = z
  .object({
    aspectRatio: z.enum([
      "1:1",
      "3:2",
      "2:3",
      "4:3",
      "3:4",
      "16:9",
      "9:16",
    ]),
    longEdge: z.union([z.literal(1024), z.literal(1536), z.literal(2048)]),
    type: z.literal("image"),
  })
  .strict();

const videoExtensionParametersSchema = z
  .object({
    aspectRatio: z.enum(["1:1", "4:3", "3:4", "16:9", "9:16", "21:9"]),
    duration: z.union([z.literal(5), z.literal(10), z.literal(15)]),
    resolution: z.enum(["768P", "2K"]),
    sound: z.enum(["with-sound", "muted"]),
    type: z.literal("video"),
  })
  .strict();

const merryChatExtensionPayloadSchema = z.union([
  z
    .object({
      definitionId: z.enum([
        "store-data",
        "daily-opportunity",
        "quick-test",
        "promotion-calendar",
      ]),
      kind: z.literal("tool"),
    })
    .strict(),
  z
    .object({
      definitionId: z.literal("product-material"),
      kind: z.literal("capability"),
      parameters: imageExtensionParametersSchema,
    })
    .strict(),
  z
    .object({
      definitionId: z.literal("marketing-video"),
      kind: z.literal("capability"),
      parameters: videoExtensionParametersSchema,
    })
    .strict(),
]);

export const merryChatExtensionsMetadataSchema = z
  .array(merryChatExtensionPayloadSchema)
  .max(10)
  .superRefine((extensions, context) => {
    const seen = new Set<string>();
    for (const [index, extension] of extensions.entries()) {
      const id = getMerryChatExtensionId(extension.kind, extension.definitionId);
      if (!seen.has(id)) {
        seen.add(id);
        continue;
      }
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "同一扩展不能重复出现",
        path: [index],
      });
    }
  });

const capabilityResourceSchema = z.object({
  resource_id: z.string().trim().min(1).max(200).regex(/^[a-zA-Z0-9_.:-]+$/u),
  resource_type: z.enum(["image", "video"]),
}).strict();

const capabilityIntentBase = {
  schema: z.literal("merchant_chat_capability_intent.v1"),
  resources: z.array(capabilityResourceSchema).max(10),
};

export const merchantChatCapabilityIntentSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      ...capabilityIntentBase,
      kind: z.literal("product-material"),
      media_kind: z.literal("image"),
      parameters: z.object({
        aspect_ratio: z.enum([
          "1:1", "3:2", "2:3", "4:3", "3:4", "16:9", "9:16",
        ]),
        requested_height: z.number().int().min(16).max(2048),
        requested_width: z.number().int().min(16).max(2048),
      }).strict(),
    }).strict().superRefine((value, context) => {
      if (value.resources.some((resource) => resource.resource_type !== "image")) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["resources"],
          message: "商品素材能力只接受图片参考资源",
        });
      }
    }),
    z.object({
      ...capabilityIntentBase,
      kind: z.literal("marketing-video"),
      media_kind: z.literal("video"),
      parameters: z.object({
        aspect_ratio: z.enum(["1:1", "4:3", "3:4", "16:9", "9:16", "21:9"]),
        duration_seconds: z.union([z.literal(5), z.literal(10), z.literal(15)]),
        generate_audio: z.boolean(),
        resolution: z.enum(["768P", "2K"]),
      }).strict(),
    }).strict().superRefine((value, context) => {
      const imageCount = value.resources.filter(
        (resource) => resource.resource_type === "image",
      ).length;
      const videoCount = value.resources.filter(
        (resource) => resource.resource_type === "video",
      ).length;
      if (imageCount > 9 || videoCount > 3) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["resources"],
          message: "营销视频参考素材数量超过 MiniMax-H3 合同",
        });
      }
    }),
  ],
);

export type MerchantChatCapabilityIntent = z.infer<
  typeof merchantChatCapabilityIntentSchema
>;

export function getMerryChatExtensionId(
  kind: MerryChatExtensionSelection["kind"],
  definitionId: string,
) {
  return `${kind}:${definitionId}`;
}

function nextOrder(selections: ReadonlyArray<MerryChatExtensionSelection>) {
  return selections.reduce(
    (highest, selection) => Math.max(highest, selection.order),
    -1,
  ) + 1;
}

export function createToolExtensionSelection(
  tool: MerryChatAddMenuTool,
  order = 0,
): MerryChatExtensionSelection {
  return {
    definitionId: tool.id,
    id: getMerryChatExtensionId("tool", tool.id),
    kind: "tool",
    order,
  };
}

export function createCapabilityExtensionSelection(
  capability: MerryChatAddMenuCapability,
  parameters?: MerryChatExtensionParameters,
  order = 0,
): MerryChatExtensionSelection {
  return {
    definitionId: capability.id,
    id: getMerryChatExtensionId("capability", capability.id),
    kind: "capability",
    order,
    ...(parameters ? { parameters } : {}),
  };
}

export function videoExtensionParameters(
  parameters: MerryVideoParameters,
): MerryChatExtensionParameters {
  return { ...parameters, type: "video" };
}

export function imageExtensionParameters(
  parameters: MerryImageParameters,
): MerryChatExtensionParameters {
  return { ...parameters, type: "image" };
}

export function upsertMerryChatExtension(
  selections: ReadonlyArray<MerryChatExtensionSelection>,
  nextSelection: MerryChatExtensionSelection,
): ReadonlyArray<MerryChatExtensionSelection> {
  const current = selections.find(
    (selection) => selection.id === nextSelection.id,
  );

  if (current) {
    return selections.map((selection) =>
      selection.id === nextSelection.id
        ? {
            ...nextSelection,
            order: current.order,
            parameters: nextSelection.parameters ?? current.parameters,
          }
        : selection,
    );
  }

  return [
    ...selections,
    { ...nextSelection, order: nextOrder(selections) },
  ];
}

export function removeMerryChatExtension(
  selections: ReadonlyArray<MerryChatExtensionSelection>,
  extensionId: string,
): ReadonlyArray<MerryChatExtensionSelection> {
  return selections
    .filter((selection) => selection.id !== extensionId)
    .map((selection, order) => ({ ...selection, order }));
}

export function updateMerryChatExtensionParameters(
  selections: ReadonlyArray<MerryChatExtensionSelection>,
  extensionId: string,
  parameters: MerryChatExtensionParameters,
): ReadonlyArray<MerryChatExtensionSelection> {
  return selections.map((selection) =>
    selection.id === extensionId ? { ...selection, parameters } : selection,
  );
}

export function formatMerryChatExtensionSummary(
  parameters?: MerryChatExtensionParameters,
) {
  if (!parameters) return undefined;

  if (parameters.type === "video") {
    return [
      parameters.aspectRatio,
      parameters.resolution,
      `${parameters.duration}秒`,
      parameters.sound === "with-sound" ? "有声" : "无声",
    ].join(" ");
  }

  return [
    parameters.aspectRatio,
    `长边${parameters.longEdge}px`,
    "1张",
    "无水印",
  ].join(" ");
}

export function buildMerryChatExtensionViewModels(
  selections: ReadonlyArray<MerryChatExtensionSelection>,
  catalog: MerryChatAddMenuCatalog,
): ReadonlyArray<MerryChatExtensionViewModel> {
  return [...selections]
    .sort((left, right) => left.order - right.order)
    .flatMap<MerryChatExtensionViewModel>((selection) => {
      if (selection.kind === "tool") {
        const tool = catalog.tools.find(
          (candidate) => candidate.id === selection.definitionId,
        );
        return tool
          ? [
              {
                ...selection,
                configurable: false,
                icon: tool.icon,
                label: tool.label,
              },
            ]
          : [];
      }

      const capability = catalog.capabilities.find(
        (candidate) => candidate.id === selection.definitionId,
      );
      return capability
        ? [
            {
              ...selection,
              configurable: true,
              icon: capabilityFileIcon,
              label: capability.label,
              summary: formatMerryChatExtensionSummary(selection.parameters),
            },
          ]
        : [];
    });
}

export function serializeMerryChatExtensions(
  selections: ReadonlyArray<MerryChatExtensionSelection>,
): ReadonlyArray<MerryChatExtensionPayload> {
  return [...selections]
    .sort((left, right) => left.order - right.order)
    .map(({ definitionId, kind, parameters }) => ({
      definitionId,
      kind,
      ...(parameters ? { parameters } : {}),
    }));
}

export function projectMerryChatExtensionViewModels(
  value: unknown,
): ReadonlyArray<MerryChatExtensionViewModel> {
  const parsed = merryChatExtensionsMetadataSchema.safeParse(value);
  if (!parsed.success) return [];

  return buildMerryChatExtensionViewModels(
    parsed.data.map((extension, order) => ({
      ...extension,
      id: getMerryChatExtensionId(extension.kind, extension.definitionId),
      order,
    })),
    emptyMerryChatAddMenuCatalog,
  );
}

export function buildMerchantChatCapabilityIntent(
  selections: ReadonlyArray<
    Pick<
      MerryChatExtensionSelection,
      "definitionId" | "kind" | "parameters"
    >
  >,
  resources: ReadonlyArray<ChatAttachmentResourceRef>,
): MerchantChatCapabilityIntent | undefined {
  const capabilities = selections.filter(
    (selection) => selection.kind === "capability",
  );
  if (capabilities.length === 0) return undefined;
  if (capabilities.length !== 1) {
    throw new ChatInputError("每轮只能选择一个媒体能力");
  }

  const capability = capabilities[0]!;
  if (
    capability.definitionId === "product-material" &&
    capability.parameters?.type === "image"
  ) {
    if (resources.some((resource) => resource.resource_type !== "image")) {
      throw new ChatInputError(
        "商品素材能力只支持图片附件，请移除文件或视频后再发送",
      );
    }
    const dimensions = merryImageOutputDimensions(capability.parameters);
    return merchantChatCapabilityIntentSchema.parse({
      schema: "merchant_chat_capability_intent.v1",
      kind: "product-material",
      media_kind: "image",
      parameters: {
        aspect_ratio: capability.parameters.aspectRatio,
        requested_height: dimensions.height,
        requested_width: dimensions.width,
      },
      resources,
    });
  }
  if (
    capability.definitionId === "marketing-video" &&
    capability.parameters?.type === "video"
  ) {
    if (resources.some((resource) => resource.resource_type === "file")) {
      throw new ChatInputError(
        "营销视频能力只支持图片或视频附件，请移除普通文件后再发送",
      );
    }
    return merchantChatCapabilityIntentSchema.parse({
      schema: "merchant_chat_capability_intent.v1",
      kind: "marketing-video",
      media_kind: "video",
      parameters: {
        aspect_ratio: capability.parameters.aspectRatio,
        duration_seconds: capability.parameters.duration,
        generate_audio: capability.parameters.sound === "with-sound",
        resolution: capability.parameters.resolution,
      },
      resources,
    });
  }
  throw new ChatInputError("所选媒体能力缺少有效参数");
}
